import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { formatCadenceLong, formatRecentTimestamp, formatRunTimestamp } from './automations/scheduleFormat.ts';
import { withIcon } from '../../core/components/Icon.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { url } from '../../core/utils/link.ts';
import { AUTOMATION_PATH } from '../constants.ts';

const WarningIcon = withIcon(faTriangleExclamation);

const DIVIDER_CLASS = 'h-px min-w-px flex-1 bg-line-weak';

const MarkerLine = ({
    label,
    date,
    failed = false,
    explanation = null,
    withYear = true,
    scheduleId = null,
}: {
    label: string;
    date?: string | null;
    failed?: boolean;
    explanation?: string | null;
    withYear?: boolean;
    scheduleId?: string | null;
}) => {
    const { t } = useTranslation('chat');
    return (
        <div className="my-5 flex flex-col gap-4">
            <div className="flex items-center gap-3">
                <span className={DIVIDER_CLASS} />
                <span
                    className={classnames(
                        'flex min-w-0 items-center gap-1.5 text-center text-xs leading-5',
                        failed ? 'text-content-danger-strong' : 'text-content-medium'
                    )}
                >
                    {failed && <WarningIcon className="flex-none" />}
                    <span>
                        {date
                            ? `${label} · ${withYear ? formatRunTimestamp(date) : formatRecentTimestamp(date)}`
                            : label}
                    </span>
                    {scheduleId && (
                        <Link
                            to={url(AUTOMATION_PATH, { scheduleId })}
                            className="flex-none font-semibold text-content-brand-strong no-underline"
                        >
                            {t('thread.viewSchedule')}
                        </Link>
                    )}
                </span>
                <span className={DIVIDER_CLASS} />
            </div>
            {explanation && <p className="m-0 text-center text-xs leading-5 text-content-medium">{explanation}</p>}
        </div>
    );
};

export const ScheduleCreatedMarker = ({ schedule }) => {
    const { t } = useTranslation('chat');
    return (
        <MarkerLine
            label={`${t('schedule.createdMarker')} · ${formatCadenceLong(t, schedule)}`}
            date={schedule.createdAt}
            withYear={false}
            scheduleId={schedule.id}
        />
    );
};

export const ScheduleRunningMarker = ({ startedAt }: { startedAt: string }) => {
    const { t } = useTranslation('chat');
    return <MarkerLine label={t('schedule.runMarker')} date={startedAt} />;
};

export const ScheduledRunMarker = ({ message, reply = null, autoPaused = false }) => {
    const { t } = useTranslation('chat');
    const failed = reply?.scheduleRunStatus === 'failed';
    const test = Boolean(reply?.scheduleTestRun);
    const code = reply?.scheduleRunError || 'RUN_FAILED';
    const error = failed
        ? t(`schedule.runErrorsInChat.${code}`, { defaultValue: t('schedule.runErrorsInChat.RUN_FAILED') })
        : '';

    let label = t(test ? 'schedule.testRunMarker' : 'schedule.runMarker');
    let explanation: string | null = null;
    if (failed && autoPaused && !test) {
        label = t('schedule.autoPausedMarker');
        explanation = t('schedule.autoPausedExplanation');
    } else if (failed) {
        label = t(test ? 'schedule.testRunFailedMarker' : 'schedule.runFailedMarker');
        explanation = test ? error : `${error} ${t('schedule.nextRunAsScheduled')}`;
    }

    return (
        <MarkerLine
            label={label}
            date={message.createdAt}
            failed={failed}
            explanation={explanation}
            withYear={!test}
            scheduleId={message.schedule?.id}
        />
    );
};
